/**
 * The signed-in user's own account.
 *
 * Shows what the administrator set (plan, expiry, status) and lets the user
 * change the one thing they own: the password. A password change revokes every
 * other session server-side, so the form says so.
 */

import { FormEvent, useState } from 'react';
import { KeyRound, Loader2, LogOut, ShieldCheck } from 'lucide-react';
import { useAuth } from './AuthContext';
import { ApiError, api, daysLeft, formatDate } from './api';
import { Modal } from '../ui/widgets/common';

export function AccountDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const { user, access, logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const remaining = daysLeft(user?.expiresAt);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    if (next !== confirm) {
      setError('The new passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await api('/api/auth/password', { method: 'POST', body: { current, next } });
      setCurrent('');
      setNext('');
      setConfirm('');
      setNotice('Password changed. Other sessions were signed out.');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not change the password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Account" onClose={onClose} width={520}>
      <div className="account-head">
        <div className="account-avatar">{(user?.username ?? '?').slice(0, 1).toUpperCase()}</div>
        <div>
          <strong>{user?.username}</strong>
          <span>
            {user?.role === 'admin' ? 'Administrator' : 'Member'} · {user?.plan ?? '—'}
          </span>
        </div>
        <span className={`badge ${access?.ok ? 'good' : 'bad'}`}>
          {access?.ok ? 'Active' : (access?.code ?? 'Limited')}
        </span>
      </div>

      <dl className="account-facts">
        <div>
          <dt>Plan</dt>
          <dd>{user?.plan ?? '—'}</dd>
        </div>
        <div>
          <dt>Renews / expires</dt>
          <dd>
            {user?.expiresAt ? formatDate(user.expiresAt) : 'No expiry'}
            {remaining !== null && remaining >= 0 ? <em> · {remaining} day(s) left</em> : null}
          </dd>
        </div>
        <div>
          <dt>Member since</dt>
          <dd>{formatDate(user?.createdAt)}</dd>
        </div>
        <div>
          <dt>Last sign-in</dt>
          <dd>{formatDate(user?.lastLoginAt)}</dd>
        </div>
      </dl>

      <form className="account-form" onSubmit={submit}>
        <h3>
          <KeyRound size={14} /> Change password
        </h3>
        <div className="field-pair">
          <label className="field">
            <span>Current</span>
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
              required
            />
          </label>
          <label className="field">
            <span>New</span>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(event) => setNext(event.target.value)}
              minLength={8}
              required
            />
          </label>
        </div>
        <label className="field">
          <span>Repeat new</span>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            minLength={8}
            required
          />
        </label>
        {error ? <p className="form-note error">{error}</p> : null}
        {notice ? <p className="form-note success">{notice}</p> : null}
        <div className="row">
          <span className="hint-text">Changing it signs out every other browser.</span>
          <span className="spacer" />
          <button className="btn primary" type="submit" disabled={busy}>
            {busy ? <Loader2 size={14} className="spin" /> : <ShieldCheck size={14} />} Update password
          </button>
        </div>
      </form>

      <div className="account-signout">
        <button className="btn" onClick={() => void logout()}>
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </Modal>
  );
}
